// ===== GROUP 5 (Task 5.2): useAlertCalendar hook =====
// Recurring logic per: digitori360-docs/frontend/estimates/feed-back.md
import { useCallback, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import Api from 'services/api'
import useSWR from 'swr'
import {
  Alert,
  AlertCalendarEvent,
  AlertCalendarQueryParams
} from 'types/alert'

interface ApiAlertResponse {
  id: number
  title: string
  content?: string
  alert_datetime: string
  end_datetime?: string
  annotation_type: number
  building_id: number
  floor_id?: number
  annotation_id?: number
  folder_id?: number
  children_id?: number
  repeat_schedule?: any
  created_at: string
  updated_at: string
  // Recurring (API schema per feed-back spec)
  send_at?: string
  repeat_type?: number // 1=none, 2=repeat
  repeat_unit?: number // 1=weekly, 2=monthly, 3=yearly
  repeat_unit_type?: number // interval quantity (e.g. 1, 2, 3...)
  repeat_week_days?: number[] // 0=Sun..6=Sat
  end_type?: number // 1=never, 2=on_date, 3=after_occurrences
  end_date?: string | null
  end_count?: number | null
}

// Helper to map snake_case backend API data => Alert frontend model
function mapApiToAlertModel(apiData: any): Alert {
  return {
    id: apiData.id,
    title: apiData.title,
    content: apiData.content,
    alertDateTime:
      apiData.alert_datetime ||
      apiData.alertDateTime ||
      apiData.send_at ||
      apiData.sendAt,
    // Note: Do NOT fall back to end_date as endDateTime.
    // end_date is the series stopping condition, not the time bounding limit for a single occurrence.
    endDateTime: apiData.end_datetime || apiData.endDateTime,
    annotationType:
      apiData.annotation?.type ||
      apiData.annotation_type ||
      apiData.annotationType,
    buildingId: apiData.building_id || apiData.buildingId,
    floorId: apiData.floor_id || apiData.floorId,
    pointId: apiData.point_id || apiData.pointId,
    annotationId: apiData.annotation_id || apiData.annotationId,
    folderId: apiData.folder_id || apiData.folderId,
    childrenId: apiData.children_id || apiData.childrenId,
    repeatSchedule: apiData.repeat_schedule || apiData.repeatSchedule,
    users: (apiData.alert_users || apiData.users)?.map((u: any) => ({
      userId: u.id ?? u.user_id,
      email: u.email,
      permission: u.permission ?? 'view',
      name: u.last_name || u.lastName || u.name,
      organizationName:
        u.organization?.name || u.organization_name || u.organizationName
    })),
    createdAt: apiData.created_at || apiData.createdAt,
    updatedAt: apiData.updated_at || apiData.updatedAt
  }
}

// Color map by annotation type — using Digitori color system
const ANNOTATION_TYPE_COLOR: Record<number | string, string> = {
  10: '#454DFF', // ARCHITECTURE
  20: '#02EADC', // AIR_CONDITION
  30: '#F1AE00', // ELECTRICITY
  40: '#4B4B4B', // MEMO
  41: '#4B4B4B', // DOCUMENT
  50: '#FF4242', // FIRE
  60: '#015CB9', // FLOOD
  70: '#EDE054', // EARTHQUAKE
  80: '#64A45F', // VOLCANO
  90: '#4B4B4B', // TRADITION
  100: '#A074F8', // EQUIPMENT
  110: '#FE7F00', // LIFE_LINE
  120: '#4B4B4B', // IMAGE
  130: '#4B4B4B', // MOVIE
  140: '#4B4B4B', // INFO
  DEFAULT: '#3163b7' // shimizu primary blue
}

// Convert Alert => FullCalendar event format (single occurrence)
function alertToCalendarEvent(
  alert: Alert,
  startIso?: string,
  eventSuffix?: string
): AlertCalendarEvent {
  const color =
    ANNOTATION_TYPE_COLOR[alert.annotationType ?? 'DEFAULT'] ??
    ANNOTATION_TYPE_COLOR.DEFAULT
  const start = startIso ?? alert.alertDateTime

  return {
    id: eventSuffix ? `${alert.id}-${eventSuffix}` : String(alert.id),
    title: alert.title,
    start,
    end: alert.endDateTime,
    extendedProps: {
      alertId: alert.id,
      content: alert.content,
      buildingId: alert.buildingId,
      floorId: alert.floorId,
      pointId: alert.pointId,
      annotationId: alert.annotationId,
      annotationType: alert.annotationType,
      folderId: alert.folderId,
      childrenId: alert.childrenId,
      repeatSchedule: alert.repeatSchedule,
      users: alert.users
    },
    backgroundColor: color,
    borderColor: color,
    textColor: '#ffffff'
  }
}

/**
 * Get occurrence dates for one alert in [rangeStart, rangeEnd] per feed-back spec.
 * - repeat_type 1: no repeat → only send_at if in range.
 * - repeat_type 2 + repeat_unit_type 1 (weekly): first occurrence >= send_at on repeat_week_days, then every repeat_unit weeks.
 */
function getOccurrenceDates(
  api: ApiAlertResponse,
  rangeStart: Dayjs,
  rangeEnd: Dayjs
): string[] {
  const sendAtStr = api.send_at || api.alert_datetime
  if (!sendAtStr) return []

  const sendAt = dayjs(sendAtStr)
  const repeatType = api.repeat_type ?? 1
  const repeatUnit = api.repeat_unit_type ?? 1 // 1=Weekly, 2=Monthly, 3=Yearly
  const interval = api.repeat_unit ?? 1 // Quantity/Interval
  const endType = api.end_type ?? 1
  const endDateStr = api.end_date
  // Normalize comparison boundaries to start of day for dates
  const sendAtDateOnly = sendAt.startOf('day')
  const endDateDateOnly = endDateStr ? dayjs(endDateStr).startOf('day') : null
  const endCount = api.end_count ?? 0
  const weekDays = Array.isArray(api.repeat_week_days)
    ? [...api.repeat_week_days].sort()
    : []

  // CASE 1: No Repeat
  if (repeatType === 1) {
    if (sendAt.isBefore(rangeStart) || sendAt.isAfter(rangeEnd)) return []
    return [sendAt.toISOString()]
  }

  // CASE 2: Weekly Repeat (per spec)
  if (repeatUnit === 1) {
    if (weekDays.length === 0) return []

    const results: string[] = []
    let currentCount = 0

    // Step 1: Define Anchor Week (week containing send_at)
    // NOTE: Force day(0) to get Sunday regardless of dayjs locale (which might make startOf('week') into Monday)
    let currentWeekStart = sendAt.day(0).startOf('day')

    // Safety breakers
    const MAX_CYCLES = 500
    let done = false

    Array.from({ length: MAX_CYCLES }).some(() => {
      if (done) return true

      // Step 2: Week Evaluation Loop
      weekDays.forEach((specDay) => {
        if (done) return

        // Calculate exact date for this weekday in current week
        // specDay is 0=Sun..6=Sat. currentWeekStart is already Sunday (day 0).
        // So offset is simply specDay.
        const currentDate = currentWeekStart.add(specDay, 'day')
        const currentDateOnly = currentDate.startOf('day')

        // Filter 1 (Past Dates): Must be >= send_at (date comparison)
        if (!currentDateOnly.isBefore(sendAtDateOnly)) {
          // Filter 2 (On Date End): Stop if we passed end_date
          if (
            endType === 2 &&
            endDateDateOnly &&
            currentDateOnly.isAfter(endDateDateOnly)
          ) {
            done = true
            return
          }

          // Filter 3 (Occurrence End): Stop if we reached end_count
          if (endType === 3 && endCount > 0 && currentCount >= endCount) {
            done = true
            return
          }

          // If in range, add to results
          // Note: The result list should contain dates in range [rangeStart, rangeEnd]
          // But currentCount must track ALL occurrences from send_at for end_type=3
          const occWithTime = currentDate
            .hour(sendAt.hour())
            .minute(sendAt.minute())
            .second(sendAt.second())
            .millisecond(sendAt.millisecond())

          if (
            !occWithTime.isBefore(rangeStart) &&
            occWithTime.isBefore(rangeEnd.add(1, 'day'))
          ) {
            results.push(occWithTime.toISOString())
          }

          currentCount += 1

          // Check endCount again after increment
          if (endType === 3 && endCount > 0 && currentCount >= endCount) {
            done = true
          }
        }
      })

      if (done) return true

      // Step 3: Interval Jump
      currentWeekStart = currentWeekStart.add(interval, 'week')

      // Optimization: if the entire week is beyond rangeEnd and beyond endDate, we can stop
      if (currentWeekStart.isAfter(rangeEnd)) {
        if (endType !== 3) {
          // If not counting occurrences, we can stop if we passed range and end_date
          if (endType === 1) {
            done = true
            return true
          }
          if (
            endType === 2 &&
            endDateDateOnly &&
            currentWeekStart.isAfter(endDateDateOnly)
          ) {
            done = true
            return true
          }
        }
      }
      return false
    })

    return results
  }

  // CASE 3 & 4: Monthly or Yearly Repeat
  if (repeatUnit === 2 || repeatUnit === 3) {
    const results: string[] = []
    let currentCount = 0
    let currentOccDate = sendAt
    const unitStr = repeatUnit === 2 ? 'month' : 'year'

    const MAX_CYCLES = 500
    let done = false

    Array.from({ length: MAX_CYCLES }).some(() => {
      if (done) return true

      const currentOccDateOnly = currentOccDate.startOf('day')

      // Filter 2 (On Date End): Stop if we passed end_date
      if (
        endType === 2 &&
        endDateDateOnly &&
        currentOccDateOnly.isAfter(endDateDateOnly)
      ) {
        done = true
        return true
      }

      // Filter 3 (Occurrence End): Stop if we reached end_count
      if (endType === 3 && endCount > 0 && currentCount >= endCount) {
        done = true
        return true
      }

      // Add to results if in range [rangeStart, rangeEnd]
      if (
        !currentOccDate.isBefore(rangeStart) &&
        currentOccDate.isBefore(rangeEnd.add(1, 'day'))
      ) {
        results.push(currentOccDate.toISOString())
      }

      currentCount += 1

      // Check endCount again after incrementing
      if (endType === 3 && endCount > 0 && currentCount >= endCount) {
        done = true
      }

      if (done) return true

      // Interval Jump
      currentOccDate = currentOccDate.add(interval, unitStr)

      // Optimization: if we moved completely past the viewing window, we can safely stop processing.
      // (Future dates will also be out of range)
      if (currentOccDate.startOf('day').isAfter(rangeEnd.startOf('day'))) {
        done = true
        return true
      }

      return false
    })

    return results
  }

  // TODO: Add Daily, Monthly, Yearly if needed in future
  // For now, fallback to single occurrence if in range
  if (sendAt.isBefore(rangeStart) || sendAt.isAfter(rangeEnd)) return []
  return [sendAt.toISOString()]
}

// ---- Main hook ----
export default function useAlertCalendar(params: AlertCalendarQueryParams) {
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)

  // SWR key — building_id is required. If params missing (direct reload of /alert-calendar) use 1947/4496 for mock display
  const swrKey = useMemo(() => {
    const buildingId = params.buildingId ?? 1947
    const p: Record<string, number> = {
      year: params.year,
      month: params.month,
      buildingId
    }
    p.floorId = params.floorId ?? 4496
    return { url: 'v1/alerts', params: p }
  }, [params.year, params.month, params.buildingId, params.floorId])

  const fetcher = useCallback(
    async (key: { url: string; params: Record<string, number> }) => {
      const { buildingId } = key.params
      if (!buildingId) return []

      const res = await Api.get<
        { edges: ApiAlertResponse[] } | ApiAlertResponse[]
      >(key.url, key.params)
      return res
    },
    []
  )

  const { data, error, isLoading, mutate } = useSWR<
    { edges: ApiAlertResponse[] } | ApiAlertResponse[]
  >(swrKey, fetcher)

  const events = useMemo(() => {
    if (!data) return []
    const rawList = Array.isArray(data)
      ? data
      : data?.edges ?? (data as any)?.data ?? []
    const year = params.year ?? dayjs().year()
    const month = params.month ?? dayjs().month() + 1
    const rangeStart = dayjs(`${year}-${String(month).padStart(2, '0')}-01`)
    const rangeEnd = rangeStart.endOf('month')

    const result: AlertCalendarEvent[] = []
    ;(rawList as ApiAlertResponse[]).forEach((item) => {
      const alert = mapApiToAlertModel(item)
      const occurrenceDates = getOccurrenceDates(item, rangeStart, rangeEnd)
      if (occurrenceDates.length === 0) return
      occurrenceDates.forEach((iso) => {
        const suffix =
          occurrenceDates.length > 1
            ? dayjs(iso).format('YYYYMMDDHHmm')
            : undefined
        result.push(alertToCalendarEvent(alert, iso, suffix))
      })
    })
    return result
  }, [data, params.year, params.month])

  const refetch = useCallback(
    () => mutate(undefined, { revalidate: true }),
    [mutate]
  )

  const getAlertById = useCallback(
    (id: number): Alert | undefined => {
      if (!data) return undefined
      const rawList = Array.isArray(data)
        ? data
        : data?.edges ?? (data as any)?.data ?? []
      const found = rawList.find((a: any) => a.id === id)
      return found ? mapApiToAlertModel(found) : undefined
    },
    [data]
  )

  return {
    events,
    isLoading,
    error: error ? 'データの取得に失敗しました' : null,
    selectedAlert,
    setSelectedAlert,
    getAlertById,
    refetch
  }
}
